import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { getAuthToken } from "../../utils/api";

const LoginPage = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = (location.state as any)?.message;
  const { error, loading, mfa, login, confirmMfa } = useAuth();

  useEffect(() => {
    if (getAuthToken()) {
      navigate("/encounters");
    }
  }, [navigate]);

  // Step 1: Doctor submits email + password
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    console.log("[LoginPage] handleSubmit called, username:", username);
    if (!username || !password) {
      console.log("[LoginPage] Missing username or password, aborting");
      return;
    }
    try {
      console.log("[LoginPage] Calling login()...");
      const result = await login(username, password);
      console.log("[LoginPage] login() returned:", result);
      if (result?.status === "success") {
        console.log("[LoginPage] ✅ Navigating to /encounters");
        navigate("/encounters");
      } else {
        console.log("[LoginPage] Result status is not 'success':", result?.status);
      }
      // If mfa_required, the form will re-render to show the MFA input
    } catch (err) {
      console.error("[LoginPage] ❌ handleSubmit caught error:", err);
      // Error is handled by useAuth
    }
  };

  // Step 2: Doctor submits their MFA code
  const handleMfaSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!mfaCode) return;
    try {
      const result = await confirmMfa(mfaCode);
      if (result?.status === "success") {
        navigate("/encounters");
      }
    } catch {
      // Error is handled by useAuth
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-100 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 md:p-10">
        <h2 className="text-2xl font-bold text-blue-700 mb-2 text-center">
          Sign In to DocFi
        </h2>

        {/* ── Step 1: Credentials ── */}
        {!mfa.required && (
          <>
            {successMessage && (
              <div className="bg-green-100 text-green-700 rounded p-2 mb-4 text-center text-sm">
                {successMessage}
              </div>
            )}
            <p className="text-gray-600 mb-2 text-center">
              Welcome back! Please enter your credentials to continue.
            </p>
            <p className="text-sm text-blue-600 mb-4 text-center font-medium">
              For medical professionals only
            </p>
            {error && (
              <div className="bg-red-100 text-red-700 rounded p-2 mb-4 text-center text-sm">
                {error}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Email"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <input
                type={isPasswordVisible ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <div className="flex items-center">
                <input
                  id="show-password"
                  type="checkbox"
                  checked={isPasswordVisible}
                  onChange={() => setIsPasswordVisible((v) => !v)}
                  className="mr-2"
                />
                <label htmlFor="show-password" className="text-sm text-gray-600">
                  Show Password
                </label>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-sm text-blue-600 hover:underline"
              >
                Forgot password?
              </button>
            </div>
            <div className="mt-4 text-center text-sm text-gray-600">
              Don&apos;t have an account?{" "}
              <button
                className="text-blue-600 hover:underline font-semibold"
                onClick={() => navigate("/register")}
                type="button"
              >
                Create Account
              </button>
            </div>
          </>
        )}

        {/* MFA TEMPORARILY DISABLED — re-enable once SNS sandbox exit is approved */}
        {/* {mfa.required && (
          <>
            <p className="text-gray-600 mb-4 text-center">
              {mfa.type === "SMS_MFA"
                ? `A verification code has been sent to ${mfa.destination ?? "your phone"}.`
                : "Enter the code from your Authenticator App."}
            </p>
            {error && (
              <div className="bg-red-100 text-red-700 rounded p-2 mb-4 text-center text-sm">
                {error}
              </div>
            )}
            <form onSubmit={handleMfaSubmit} className="space-y-4">
              <input
                type="text"
                placeholder="Enter 6-digit code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                maxLength={6}
                required
                autoFocus
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 text-center text-2xl tracking-widest"
              />
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50"
              >
                {loading ? "Verifying..." : "Verify Code"}
              </button>
            </form>
          </>
        )} */}
      </div>
    </div>
  );
};

export default LoginPage;
